function trimStr(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
}

/**
 * Launch URL from game-style GET success bodies (`/games/url`, `/forest-data`,
 * `/galone-data`, `/galone-galone`, `/bugyee-data`, `/shankomee-data`, etc.).
 * Prefers `gameUrl` (flat or under `data`). Legacy `url` is accepted if present.
 */
export function getGameUrlFromGamesUrlResponse(res) {
  const root = res && res.data;
  if (!root || typeof root !== 'object') return '';

  const nested = root.data && typeof root.data === 'object' ? root.data : null;

  const gameUrl = trimStr(root.gameUrl) || (nested && trimStr(nested.gameUrl));
  if (gameUrl) return gameUrl;

  return trimStr(root.url) || (nested && trimStr(nested.url));
}

/**
 * Text for HTTP 200 responses that omit a launch URL (business rules / soft errors).
 * Combines `message` and `description` when both exist.
 */
export function getGameLaunchBusinessMessage(res) {
  const root = res && res.data;
  if (!root || typeof root !== 'object') return '';

  let nested = null;
  if (root.data != null) {
    if (typeof root.data === 'object' && !Array.isArray(root.data)) {
      nested = root.data;
    } else if (Array.isArray(root.data) && root.data[0] && typeof root.data[0] === 'object') {
      nested = root.data[0];
    }
  }

  const message =
    trimStr(root.message) ||
    trimStr(root.msg) ||
    (typeof root.error === 'string' && trimStr(root.error)) ||
    (nested && trimStr(nested.message)) ||
    (nested && trimStr(nested.msg));
  const description =
    trimStr(root.description) ||
    (nested && trimStr(nested.description)) ||
    trimStr(root.detail);

  if (message && description && message !== description) {
    return `${message} — ${description}`;
  }
  if (description) return description;
  if (message) return message;
  return '';
}

/**
 * Dialog body when HTTP 200 has no launch URL: prefer API message/description, else generic copy.
 */
export function getGameUrlMissingLaunchBody(res, lang = 'en') {
  const fromApi = getGameLaunchBusinessMessage(res);
  if (fromApi) return fromApi;
  const isEn = lang === 'en';
  return isEn
    ? 'Game link missing from response.'
    : 'ဂိမ်းလင့်ခ် မရရှိပါ။';
}

/**
 * Parsed API error body for `/games/url` (and similar) failures.
 */
export function parseGameUrlApiError(error) {
  const data = error && error.response && error.response.data;
  const fallbackDefault = 'Unable to open the game. Please try again.';

  if (typeof data === 'string') {
    const s = trimStr(data);
    return {
      message: '',
      description: s,
      rawBody: s,
      networkMessage: trimStr(error && error.message),
      fallbackDefault,
    };
  }

  if (!data || typeof data !== 'object') {
    return {
      message: '',
      description: '',
      rawBody: '',
      networkMessage: trimStr(error && error.message),
      fallbackDefault,
    };
  }

  const nested = data.data && typeof data.data === 'object' ? data.data : null;

  const message =
    trimStr(data.message) ||
    (nested && trimStr(nested.message)) ||
    trimStr(data.error);

  const description =
    trimStr(data.description) ||
    (nested && trimStr(nested.description)) ||
    trimStr(data.error_description);

  return {
    message,
    description,
    rawBody: '',
    networkMessage: trimStr(error && error.message),
    fallbackDefault,
  };
}

/**
 * Multi-line text for error dialog (shows description when separate from message).
 */
export function getGameUrlApiErrorDialogBody(error, fallbackMessage, lang = 'en') {
  const isEn = lang === 'en';
  const fallback =
    trimStr(fallbackMessage) ||
    (isEn ? 'Unable to open the game. Please try again.' : 'ဂိမ်းဖွင့်၍မရပါ။ ထပ်စမ်းကြည့်ပါ။');

  const p = parseGameUrlApiError(error);
  if (p.rawBody) return p.rawBody || fallback;

  const { message, description } = p;
  if (message && description && message !== description) {
    return isEn
      ? `Message\n${message}\n\nDescription\n${description}`
      : `Message\n${message}\n\nDescription\n${description}`;
  }
  if (message) return message;
  if (description) return description;
  return p.networkMessage || fallback;
}

/**
 * User-visible text for failed game launch GETs (same shape as `/games/url` errors).
 * Prefer API `message` and include `description` when the backend sends both.
 */
export function getGameUrlApiErrorMessage(error, fallbackMessage) {
  const fallback =
    trimStr(fallbackMessage) ||
    'Unable to open the game. Please try again.';
  const p = parseGameUrlApiError(error);

  if (p.rawBody) return p.rawBody || fallback;

  const { message, description } = p;
  if (message && description && message !== description) {
    return `${message} — ${description}`;
  }
  if (message) return message;
  if (description) return description;

  return p.networkMessage || fallback;
}
